import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  BookOpen,
  Code2,
  MessageSquare,
  Palette,
  Plus,
  RefreshCw,
  Settings2,
  Sparkles,
} from 'lucide-react';
import { Badge, Button, Card, CardContent, Skeleton, Switch, cn, useToast } from '@respectly/ui';
import { useChatWidgetsStore } from '../../stores/useChatWidgetsStore';
import { KnowledgePicker } from './KnowledgePicker';
import { BrandDocuments } from './BrandDocuments';
import { PresentationSection } from './PresentationSection';
import { BehaviourSection, AvailabilityBadge } from './BehaviourSection';
import { WidgetPreview } from './WidgetPreview';
import { CreateWidgetDialog } from './CreateWidgetDialog';

const container = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.06 } } };
const item = { hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

type TabKey = 'knowledge' | 'presentation' | 'behaviour' | 'embed';
const TABS: { key: TabKey; label: string; icon: typeof BookOpen }[] = [
  { key: 'knowledge', label: 'Knowledge', icon: BookOpen },
  { key: 'presentation', label: 'Presentation', icon: Palette },
  { key: 'behaviour', label: 'Behaviour', icon: Settings2 },
  { key: 'embed', label: 'Embed', icon: Code2 },
];

/**
 * Website chat widgets.
 *
 * Each widget embedded on a public site gets its own knowledge source — the
 * markdown documents it may answer from — plus its own sample prompts, greeting
 * and palette. That configuration used to live in Cosmos DB and change only by
 * redeploying a seed script, which is how production drifted from anything
 * reviewed.
 */
export function ChatWidgets() {
  const store = useChatWidgetsStore();
  const { toast } = useToast();
  const navigate = useNavigate();
  // `strict: false` because the route tree nests this page under the layout
  // route, so the literal path is not a valid `from` id.
  const search = useSearch({ strict: false }) as { widget?: string };
  const [tab, setTab] = useState<TabKey>('knowledge');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    // Open the widget the URL names. Without this a reload always landed on the
    // first widget in the list, and its empty document panel read as though the
    // documents uploaded to a *different* widget had been lost.
    void store.load(search.widget ?? null);
    void store.loadDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the URL pointing at whatever is open, so reload and back both return
  // here. `replace` because switching widgets is not a navigation worth a history
  // entry.
  const selected = store.selectedBrandId;
  useEffect(() => {
    if (!selected || selected === search.widget) return;
    void navigate({ to: '/chat-widgets', search: { widget: selected }, replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // The uploaded documents belong to the widget, not to the Knowledge tab. They
  // used to be fetched by `BrandDocuments`, which only mounts on that tab — so
  // every other tab read a document count of zero, and the knowledge picker drew
  // its "answers from nothing" verdict from it.
  useEffect(() => {
    if (selected) void store.loadDocuments(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const brand = store.brands.find((b) => b.brandId === store.selectedBrandId) ?? null;
  const dirty = Object.keys(store.draft).length > 0;

  const save = async () => {
    const ok = await store.save();
    toast(
      ok
        ? { title: 'Widget saved', description: 'Live within a second — the runtime cache was cleared.' }
        : { title: 'Could not save', description: store.error ?? 'Something went wrong.', variant: 'error' }
    );
  };

  return (
    <motion.div variants={container} initial="hidden" animate="visible" className="space-y-6 p-1">
      {/* Header */}
      <motion.div variants={item} className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-gradient-to-br from-teal-600 to-cyan-500 p-2.5 text-white shadow-lg shadow-teal-500/20">
            <MessageSquare className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-50">
              Chat Widgets
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              The assistant embedded on public sites. Each widget answers only from the documents
              you choose for it.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void store.load();
              void store.loadDocs();
            }}
          >
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Refresh
          </Button>
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus className="mr-1.5 h-3.5 w-3.5" /> New widget
          </Button>
        </div>
      </motion.div>

      {store.error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
          {store.error}
        </div>
      )}

      {store.loading && store.brands.length === 0 ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
      ) : store.brands.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Sparkles className="mx-auto mb-3 h-8 w-8 text-slate-300" />
            <p className="text-sm font-medium text-slate-700 dark:text-slate-200">No widgets yet</p>
            <p className="mx-auto mt-1 max-w-md text-sm text-slate-500 dark:text-slate-400">
              Create one per site that should embed the assistant, then choose the documents it may
              answer from.
            </p>
            <Button className="mt-4" size="sm" onClick={() => setCreating(true)}>
              <Plus className="mr-1.5 h-3.5 w-3.5" /> New widget
            </Button>
          </CardContent>
        </Card>
      ) : (
        <motion.div variants={item} className="grid gap-5 lg:grid-cols-[260px_1fr]">
          {/* Widget list */}
          <div className="space-y-1.5">
            {store.brands.map((b) => {
              const active = b.brandId === store.selectedBrandId;
              return (
                <button
                  key={b.brandId}
                  onClick={() => store.select(b.brandId)}
                  className={cn(
                    'w-full rounded-xl border p-3 text-left transition-colors',
                    active
                      ? 'border-teal-400 bg-teal-50/70 dark:border-teal-700 dark:bg-teal-950/25'
                      : 'border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:hover:bg-slate-800/60'
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                      {b.displayName}
                    </span>
                    {!b.isActive && (
                      <Badge variant="secondary" className="shrink-0 text-[10px]">
                        Off
                      </Badge>
                    )}
                  </div>
                  <p className="mt-0.5 truncate font-mono text-[11px] text-slate-400">{b.brandId}</p>
                  <KnowledgeSummary
                    curated={b.knowledgeSlugs.length}
                    own={b.ownDocumentCount}
                  />
                </button>
              );
            })}
          </div>

          {/* Detail */}
          {brand && (
            <div className="space-y-4">
              <Card>
                <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
                  <div className="flex items-center gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {brand.displayName}
                      </p>
                      <p className="font-mono text-[11px] text-slate-400">{brand.brandId}</p>
                    </div>
                    <AvailabilityBadge state={brand.agentAvailability} />
                  </div>
                  <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                    <Switch
                      checked={brand.isActive}
                      onCheckedChange={(checked) => void store.setActive(brand.brandId, checked)}
                      aria-label={`${brand.isActive ? 'Deactivate' : 'Activate'} ${brand.displayName}`}
                    />
                    {brand.isActive ? 'Live' : 'Off'}
                  </label>
                </CardContent>
              </Card>

              {!brand.isActive && (
                <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500 dark:border-slate-800 dark:bg-slate-800/50 dark:text-slate-400">
                  This widget is off. Any page embedding it gets a 404 from the chat API and shows
                  nothing — no launcher, no error.
                </p>
              )}

              <div className="grid gap-5 xl:grid-cols-[1fr_320px]">
                <div className="space-y-4">
                  {/* Tabs */}
                  <div className="flex flex-wrap gap-1 border-b border-slate-200 dark:border-slate-800">
                    {TABS.map((t) => {
                      const Icon = t.icon;
                      const isActive = tab === t.key;
                      return (
                        <button
                          key={t.key}
                          onClick={() => setTab(t.key)}
                          className={cn(
                            '-mb-px flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
                            isActive
                              ? 'border-teal-500 text-teal-700 dark:text-teal-400'
                              : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
                          )}
                        >
                          <Icon className="h-4 w-4" />
                          {t.label}
                        </button>
                      );
                    })}
                  </div>

                  <Card>
                    <CardContent className="py-5">
                      {tab === 'knowledge' && (
                        <div className="space-y-7">
                          <KnowledgePicker
                            selected={store.draft.knowledgeSlugs ?? brand.knowledgeSlugs}
                            onChange={(knowledgeSlugs) => store.edit({ knowledgeSlugs })}
                            docs={store.docs}
                            configured={store.knowledgeConfigured}
                            loading={store.loadingDocs && !store.knowledgeLoaded}
                            missing={brand.missingKnowledgeSlugs}
                            // Ticking nothing here is only "answers from nothing"
                            // if the widget has no documents of its own either.
                            ownDocumentCount={
                              store.documents.filter((d) => d.isActive).length
                            }
                            // Zero uploaded documents and "not fetched yet" are
                            // the same number; only this tells them apart.
                            documentsKnown={store.documentsLoadedFor === brand.brandId}
                          />
                          <div className="border-t border-slate-100 pt-6 dark:border-slate-800">
                            <BrandDocuments brandId={brand.brandId} />
                          </div>
                        </div>
                      )}
                      {tab === 'presentation' && (
                        <PresentationSection brand={brand} draft={store.draft} edit={store.edit} />
                      )}
                      {tab === 'behaviour' && (
                        <BehaviourSection brand={brand} draft={store.draft} edit={store.edit} />
                      )}
                      {tab === 'embed' && <EmbedSnippet brandId={brand.brandId} />}
                    </CardContent>
                  </Card>
                </div>

                {/* Preview */}
                <div className="xl:sticky xl:top-4 xl:self-start">
                  <p className="mb-2 text-[11px] font-medium uppercase tracking-wider text-slate-400">
                    Preview
                  </p>
                  <WidgetPreview brand={brand} draft={store.draft} docs={store.docs} />
                </div>
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* Save bar — only present when there is something to save */}
      {dirty && brand && (
        <div className="sticky bottom-4 z-10 flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white/95 px-4 py-3 shadow-lg backdrop-blur dark:border-slate-700 dark:bg-slate-900/95">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Unsaved changes to <strong>{brand.displayName}</strong>
          </p>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={store.discard} disabled={store.saving}>
              Discard
            </Button>
            <Button size="sm" onClick={() => void save()} disabled={store.saving}>
              {store.saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </div>
      )}

      <CreateWidgetDialog open={creating} onOpenChange={setCreating} />
    </motion.div>
  );
}

/**
 * The embed snippet. Note what is *not* in it: no theme attribute, no API URL
 * guesswork per site. Everything the widget renders comes from this page via
 * `GET /chat/v1/config`, which is the point of moving the configuration into the
 * database.
 */
function EmbedSnippet({ brandId }: { brandId: string }) {
  const snippet = `<script type="module" src="https://cdn.respectly.ai/respectly-chat.js" async></script>
<respectly-chat brand="${brandId}"></respectly-chat>`;

  return (
    <div className="space-y-3">
      <div>
        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Embed snippet</p>
        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
          Paste into the host page, before <span className="font-mono">&lt;/body&gt;</span>. The
          greeting, prompts, palette and knowledge all come from this page — the snippet never needs
          to change again.
        </p>
      </div>
      <pre className="overflow-x-auto rounded-xl bg-slate-900 p-4 text-[12px] leading-relaxed text-slate-100">
        <code>{snippet}</code>
      </pre>
      <Button
        variant="outline"
        size="sm"
        onClick={() => void navigator.clipboard.writeText(snippet)}
      >
        Copy snippet
      </Button>
      <p className="text-[11px] text-slate-400">
        The host page must be one of this widget&rsquo;s allowed origins, matched by exact host.
      </p>
    </div>
  );
}

/**
 * What a widget knows, in one line.
 *
 * Both sources count. Reading `knowledgeSlugs` alone told an admin who had just
 * uploaded a folder of their own documents that the widget had "no documents and
 * answers nothing" — which is false, and reads exactly like the upload failed.
 * Only an empty count on *both* sides is the red warning.
 */
function KnowledgeSummary({ curated, own }: { curated: number; own: number }) {
  if (curated === 0 && own === 0) {
    return (
      <p className="mt-1.5 text-[11px] font-medium text-red-600 dark:text-red-400">
        No documents — answers nothing
      </p>
    );
  }

  const parts: string[] = [];
  if (curated > 0) parts.push(`${curated} from the corpus`);
  if (own > 0) parts.push(`${own} uploaded`);

  return (
    <p className="mt-1.5 text-[11px] text-slate-500 dark:text-slate-400">
      {parts.join(' · ')}
    </p>
  );
}
